import { useMemo, useState } from "react";
import { jobsApi, ApiError } from "@/api";
import type { JobClient, ClientJobRow, ClientUpdateInput } from "@/types";
import { Button, TextField, TextAreaField, SelectField } from "@/components/ui";
import {
  lookupPostcodeAddresses,
  postcodeLookupAvailable,
  type PostcodeAddress,
} from "@/lib/postcode";
import { FormDrawer } from "./FormDrawer";

/** Short display address for the "Copy address from…" picker — the job's legacy
 *  full-address string, else its name, else "Job #<number>", prefixed with the
 *  job number when there is one. */
function jobLabel(j: ClientJobRow): string {
  const base = (j.address || j.job_name || "").trim() || `Job #${j.job_number}`;
  return j.job_number ? `${j.job_number} · ${base}` : base;
}

/** Edit a client's contact + split-address fields (Brief 02 §3.4). Sends ONLY
 *  dirty fields (the server no-ops unchanged values + only pushes Xero when a
 *  Xero-relevant field actually changed). `id` goes as a number, `is_company` as
 *  a boolean; we send the split address fields, never the composed `address`, and
 *  omit `sync_to_xero` (server defaults true). Xero outcome is informational —
 *  never blocks.
 *
 *  Two fast ways to set the address: "Copy address from…" a linked job (the
 *  primary path — most clients live at one of their job sites; shown only when
 *  `jobs` is supplied, i.e. from the client hub) and an Ideal Postcodes lookup
 *  (the fallback, always available). Backdrop click is disabled so an accidental
 *  click can't discard in-progress edits. */
export function EditClientDrawer({
  client,
  jobs,
  onClose,
  onSaved,
}: {
  client: JobClient;
  /** The client's linked jobs — enables the "Copy address from…" picker. Omit
   *  (e.g. the job-detail Clients tab) to hide it; the postcode lookup stays. */
  jobs?: ClientJobRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [first, setFirst] = useState(client.contact_first_name ?? "");
  const [last, setLast] = useState(client.contact_last_name ?? "");
  const [isCompany, setIsCompany] = useState(!!client.is_company);
  const [company, setCompany] = useState(client.company_name ?? "");
  const [email, setEmail] = useState(client.email ?? "");
  const [phone, setPhone] = useState(client.phone ?? "");
  const [line1, setLine1] = useState(client.address_line_1 ?? "");
  const [line2, setLine2] = useState(client.address_line_2 ?? "");
  const [line3, setLine3] = useState(client.address_line_3 ?? "");
  const [city, setCity] = useState(client.city ?? "");
  const [county, setCounty] = useState(client.county ?? "");
  const [postcode, setPostcode] = useState(client.postcode ?? "");
  const [notes, setNotes] = useState(client.notes ?? "");

  // Copy-from-job (primary address path).
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | undefined>();

  // Ideal Postcodes lookup (fallback) — collapsed until revealed.
  const [showLookup, setShowLookup] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<PostcodeAddress[] | null>(null);
  const [searchError, setSearchError] = useState<string | undefined>();
  const [selected, setSelected] = useState<PostcodeAddress | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /** Copy the structured address off a linked job. The clients/detail jobs[] only
   *  carries the legacy address string, so fetch the job's /detail for the split
   *  fields rather than widening the list payload. */
  async function copyFromJob(jobId: number) {
    if (!jobId || copying) return;
    setCopying(true);
    setCopyError(undefined);
    try {
      const { job } = await jobsApi.getJobDetail(jobId);
      setLine1(job.address_line_1 ?? "");
      setLine2(job.address_line_2 ?? "");
      setLine3(job.address_line_3 ?? "");
      setCity(job.city ?? "");
      setCounty(job.county ?? "");
      setPostcode(job.postcode ?? "");
      setSelected(null); // a fresh copy supersedes any prior lookup highlight
    } catch (err) {
      setCopyError(err instanceof ApiError ? err.message : "Could not load that job's address.");
    } finally {
      setCopying(false);
    }
  }

  // The result whose line_1 case-insensitively matches the typed first line —
  // the likely-correct premise, pre-highlighted (not auto-selected).
  const matchKey = line1.trim().toLowerCase();
  const matchIndex = useMemo(() => {
    if (!results || !matchKey) return -1;
    return results.findIndex((r) => r.line1.trim().toLowerCase() === matchKey);
  }, [results, matchKey]);

  async function search() {
    const q = postcode.trim();
    if (!q || searching) return;
    setSearching(true);
    setSearchError(undefined);
    setResults(null);
    setSelected(null);
    try {
      const found = await lookupPostcodeAddresses(q);
      if (found.length === 0) {
        setSearchError("No addresses found for that postcode — check it and try again.");
        return;
      }
      setResults(found);
    } catch (e) {
      setSearchError(e instanceof Error ? e.message : "Postcode lookup failed.");
    } finally {
      setSearching(false);
    }
  }

  /** Selecting a lookup result writes straight into the structured fields. */
  function chooseLookup(r: PostcodeAddress) {
    setSelected(r);
    setLine1(r.line1);
    setLine2(r.line2);
    setLine3(r.line3);
    setCity(r.postTown);
    setCounty(r.county);
    setPostcode(r.postcode);
  }

  /** Build a body of only the fields that actually changed (id always present). */
  function dirtyBody(): ClientUpdateInput {
    const changed: Record<string, string | boolean> = {};
    const text: [string, string, string | null][] = [
      ["contact_first_name", first, client.contact_first_name],
      ["contact_last_name", last, client.contact_last_name],
      ["company_name", company, client.company_name],
      ["email", email, client.email],
      ["phone", phone, client.phone],
      ["address_line_1", line1, client.address_line_1],
      ["address_line_2", line2, client.address_line_2],
      ["address_line_3", line3, client.address_line_3],
      ["city", city, client.city],
      ["county", county, client.county],
      ["postcode", postcode, client.postcode],
      ["notes", notes, client.notes],
    ];
    for (const [key, cur, orig] of text) {
      if (cur.trim() !== (orig ?? "")) changed[key] = cur.trim();
    }
    if (isCompany !== !!client.is_company) changed.is_company = isCompany;
    return { id: client.id, ...changed } as ClientUpdateInput;
  }

  async function submit() {
    if (saving) return;
    if (isCompany ? !company.trim() : !(first.trim() && last.trim())) {
      setError(isCompany ? "Enter the company name." : "Enter the client's first and last name.");
      return;
    }
    const body = dirtyBody();
    // Nothing changed → close without a pointless round-trip / misleading toast.
    if (Object.keys(body).length === 1) {
      onClose();
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      await jobsApi.updateClient(body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the client.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title="Edit client"
      busy={saving}
      closeOnOverlayClick={false}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Save client</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <h3>Contact</h3>
        <label className="lead-drawer__check">
          <input type="checkbox" checked={isCompany} onChange={(e) => setIsCompany(e.target.checked)} />
          This client is a company
        </label>
        {isCompany ? (
          <TextField label="Company name" required value={company} onChange={(e) => setCompany(e.target.value)} />
        ) : null}
        <div className="lead-drawer__two">
          <TextField label="First name" required={!isCompany} value={first} onChange={(e) => setFirst(e.target.value)} />
          <TextField label="Last name" required={!isCompany} value={last} onChange={(e) => setLast(e.target.value)} />
        </div>
        <div className="lead-drawer__two">
          <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
      </section>

      <section className="job-drawer__section">
        <h3>Address</h3>
        {jobs && jobs.length > 0 ? (
          <div className="contact-copy">
            <SelectField
              label="Copy address from…"
              value=""
              disabled={copying}
              hint="Fastest path — pull this client's address from one of their job sites."
              onChange={(e) => {
                const id = Number(e.target.value);
                if (id) copyFromJob(id);
              }}
            >
              <option value="">{copying ? "Copying…" : "Select a job…"}</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>{jobLabel(j)}</option>
              ))}
            </SelectField>
            {copyError ? (
              <p className="leads__error" role="alert" onClick={() => setCopyError(undefined)}>{copyError}</p>
            ) : null}
          </div>
        ) : null}

        <TextField label="Address line 1" value={line1} onChange={(e) => setLine1(e.target.value)} />
        <TextField label="Address line 2" value={line2} onChange={(e) => setLine2(e.target.value)} />
        <TextField label="Address line 3" value={line3} onChange={(e) => setLine3(e.target.value)} />
        <div className="lead-drawer__two">
          <TextField label="City / town" value={city} onChange={(e) => setCity(e.target.value)} />
          <TextField label="County" value={county} onChange={(e) => setCounty(e.target.value)} />
        </div>
        <TextField label="Postcode" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} />

        {!showLookup ? (
          <button
            type="button"
            className="contact-lookup__toggle"
            onClick={() => setShowLookup(true)}
            disabled={!postcodeLookupAvailable}
            title={postcodeLookupAvailable ? undefined : "Address lookup isn’t configured (no Ideal Postcodes API key)."}
          >
            Look up address by postcode
          </button>
        ) : (
          <div className="contact-lookup">
            <div className="contact-lookup__head">
              <h4>Look up address</h4>
              <button type="button" className="contact-lookup__toggle" onClick={() => setShowLookup(false)}>Hide</button>
            </div>

            <div className="addr-search">
              <TextField
                label="Postcode"
                className="addr-search__pc"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    search();
                  }
                }}
                placeholder="e.g. KT12 2YA"
              />
              <Button
                variant="secondary"
                onClick={search}
                loading={searching}
                disabled={!postcode.trim()}
                className="addr-search__btn"
              >
                Search
              </Button>
            </div>
            <p className="job-drawer__muted">Searches the postcode above; “Address line 1” highlights the likely match.</p>

            {searchError ? (
              <p className="leads__error" role="alert" onClick={() => setSearchError(undefined)}>{searchError}</p>
            ) : null}

            {results && results.length > 0 ? (
              <ul className="addr-results">
                {results.map((r, i) => {
                  const isSelected = selected === r;
                  const isMatch = i === matchIndex;
                  const cls =
                    "addr-result" +
                    (isSelected ? " addr-result--selected" : "") +
                    (isMatch && !isSelected ? " addr-result--match" : "");
                  return (
                    <li key={r.uprn || i}>
                      <button type="button" className={cls} onClick={() => chooseLookup(r)} aria-pressed={isSelected}>
                        <span className="addr-result__line">
                          {[r.line1, r.postTown].filter(Boolean).join(", ")}
                        </span>
                        {isMatch && !isSelected ? <span className="addr-result__tag">Likely match</span> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        )}
      </section>

      <section className="job-drawer__section">
        <h3>Notes</h3>
        <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </section>
    </FormDrawer>
  );
}
